import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductInquiry = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/inquiries`, formData);
      
      if (response.status === 200) {
        toast.success("Inquiry Submitted!", {
          description: "Thank you for your interest. We'll contact you soon via email.",
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          productInterest: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error("Submission Failed", {
        description: "Please try again or contact us directly at info@akiwa.tech",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="inquiry" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Request a Quote</h2>
          <p className="text-lg text-gray-600">
            Interested in our products? Share your requirements and we'll get back to you.
          </p>
        </div>

        {/* Inquiry Form */}
        <Card className="p-8 border-2 border-gray-200 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name and Email Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700 font-medium">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  className="border-gray-300 focus:border-black focus:ring-black"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  className="border-gray-300 focus:border-black focus:ring-black"
                />
              </div>
            </div>

            {/* Phone and Company Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="98317 04400"
                  className="border-gray-300 focus:border-black focus:ring-black"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-gray-700 font-medium">Company Name</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your Company"
                  className="border-gray-300 focus:border-black focus:ring-black"
                />
              </div>
            </div>

            {/* Product Interest */}
            <div className="space-y-2">
              <Label htmlFor="productInterest" className="text-gray-700 font-medium">Product Interest</Label>
              <Input
                id="productInterest"
                name="productInterest"
                value={formData.productInterest}
                onChange={handleChange}
                placeholder="e.g., Digital Standees, LED TVs, CCTV Systems"
                className="border-gray-300 focus:border-black focus:ring-black"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message" className="text-gray-700 font-medium">Your Message *</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                placeholder="Please share your requirements, quantity needed, and any specific questions..."
                className="border-gray-300 focus:border-black focus:ring-black resize-none"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-black hover:bg-gray-800 text-white py-6 text-lg transition-all duration-300 hover:scale-105 shadow-lg group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="mr-2 group-hover:translate-x-1 transition-transform" size={20} />
              {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
            </Button>
          </form>
        </Card>
      </div>
    </section>
  );
};

export default ProductInquiry;
